<template>
    <div
        id="chat-container__stream"
        ref="scrollRoot"
        tabindex="0"
        aria-label="Chat messages"
        :class="{ 'hidden md:flex': showWidgetSidebar, 'overflow-y-auto': showOverflow, 'overflow-y-hidden': !showOverflow }"
        class="grow md:flex snap-y max-md:scroll-pt-10 scroll-pb-5 md:scroll-pb-10 overflow-x-hidden focus:outline-hidden focus-visible:outline">
        <Teleport v-if="!boxed" defer to="#chat-container">
            <div ref="topFade" class="blurEffect"></div>
        </Teleport>
        <div
            :class="[
                'w-full chat_component rounded-xl',
                { 'refreshed-chat': isNadiaChatRefreshEnabled },
                boxed ? 'boxed self-start px-2 md:px-4' : 'md:max-w-(--breakpoint-md) md:mx-auto',
            ]">
            <!-- `messages` class is used as a selector in the devops repo, for synthetic checks -->
            <div :class="['relative messages', boxed ? 'max-md:px-4 pt-6' : showHeader ? 'px-6 sm:mt-10 pt-[env(safe-area-inset-top)]' : 'px-6 sm:mt-10 pt-10']">
                <!-- Boxed PM chats keep the pill in the card's stream so it stays centred on the card and never covers the title. -->
                <Teleport to="body" :disabled="boxed">
                    <Transition name="fade">
                        <div
                            v-if="showActivationPill"
                            ref="activationPill"
                            class="z-10"
                            :class="boxed ? 'sticky top-2 flex justify-center mb-4' : 'fixed top-8 inset-x-0 w-fit mx-auto'">
                            <ChatActivation :messages="messages" />
                        </div>
                    </Transition>
                </Teleport>
                <SplashScreen v-if="showHeader && !isNadiaChatRefreshEnabled" :name="userFirstName" :coaching-session-created-at="coachingSessionCreatedAt" />
                <p v-else-if="showHeader" class="scaffold-details-small text-ink-muted text-center mb-6" data-testid="nadia-chat-date">
                    {{ nadiaChatDate }}
                </p>
                <Transition name="fade">
                    <div v-if="!showStartLoadingMessage" :class="['fs-mask chat-message-stack flex flex-col', isNadiaChatRefreshEnabled ? 'gap-5' : 'gap-10']">
                        <template v-for="(message, idx) in messages" :key="idx">
                            <template v-if="message.role === 'assistant'">
                                <div v-if="hasRenderableAssistantContent(message)" :class="{ 'document-tool-message': isCompletedDocumentToolMessage(message) }">
                                    <template v-for="(line, lineIdx) in message.lines" :key="lineIdx">
                                        <div
                                            v-if="shouldRenderLine(message, line)"
                                            :ref="(ref) => setMessageRef(ref, message.chat_message_id)"
                                            :class="[
                                                'group/chat flex flex-col max-w-full transition-colors duration-300 rounded-lgr',
                                                isNadiaChatRefreshEnabled ? 'body-small' : 'nadia-chat',
                                                { 'bg-select-option-selected': message.chat_message_id === focusedMessage },
                                            ]">
                                            <!-- group/chat is needed for hover to work with ChatMessageReactionBar -->
                                            <article class="grow text-left px-2" :aria-label="`Assistant message ${idx}-${lineIdx}`">
                                                <div :class="['prose', { 'prose-p:first:mt-0 max-w-none': isNadiaChatRefreshEnabled }]">
                                                    <ChatMessageText
                                                        v-if="line.type === 'md'"
                                                        :message="line.content"
                                                        :message-id="message.chat_message_id"
                                                        :message-obj="message" />
                                                </div>
                                                <ChatMessageToolCall
                                                    v-if="shouldRenderToolCallPill(line)"
                                                    :tool="toolCallForLine(line)"
                                                    :hide-email-button="hideEmailButton"
                                                    :is-running="isToolTurnRunning(idx)" />
                                            </article>
                                            <Transition name="slide-up">
                                                <UIDirectiveRenderer
                                                    v-if="shouldShowUiDirective(line)"
                                                    :data="line"
                                                    :message-id="message.chat_message_id"
                                                    :line-idx="lineIdx"
                                                    :proactive-coaching-offer-enabled="proactiveCoachingOfferEnabled"
                                                    :proactive-feature-offer-enabled="proactiveFeatureOfferEnabled"
                                                    @started="handleRolePlayStarted"
                                                    @canceled="handleRolePlayCanceled" />
                                            </Transition>
                                            <div
                                                v-if="shouldIncludeReactionBar(message, lineIdx)"
                                                :class="[
                                                    'flex justify-between items-center px-2',
                                                    isNadiaChatRefreshEnabled && (shouldShowUiDirective(line) ? 'mt-1 -mb-2' : '-mt-3 -mb-2'),
                                                ]">
                                                <ChatMessageReactionBar
                                                    :ref="(ref) => (reactionBarRefs[`rb-${message.chat_message_id}`] = ref)"
                                                    :message="message"
                                                    :is-last-message="idx === messages.length - 1"
                                                    :has-existing-feedback="getMessageFeedbackState(message.chat_message_id).thumbState !== null"
                                                    @thought-process-clicked="handleThoughtProcessClicked" />
                                                <BaseButton
                                                    v-if="!isNadiaChatRefreshEnabled && shouldShowNadiaIntelligence(message)"
                                                    theme="tertiary"
                                                    size="sm"
                                                    class="flex gap-2 items-center"
                                                    @click="setIsNadiaIntelligenceOpen(message.chat_message_id, !isNadiaIntelligenceOpen(message.chat_message_id))">
                                                    <NadiaIntelligence class="w-5 h-5" /> Nadia Intelligence
                                                    <i
                                                        :class="['bi text-base/none', isNadiaIntelligenceOpen(message.chat_message_id) ? 'bi-chevron-up' : 'bi-chevron-down']"
                                                        aria-hidden="true"></i>
                                                </BaseButton>
                                            </div>
                                        </div>
                                    </template>
                                    <ChatNadiaIntelligence v-if="!isNadiaChatRefreshEnabled" :message="message" />
                                </div>
                                <GuidedSetupNewChatOffer
                                    v-if="guidedSetupNewChatOffer && shouldShowNewChatOfferAfter(message, idx)"
                                    :offer="guidedSetupNewChatOffer"
                                    :coaching-session-uuid="coachingSessionUuid"
                                    class="mx-2 my-4"
                                    @dismiss="emit('guidedSetupOfferDismissed')" />
                            </template>
                            <template v-else-if="message.role === 'user'">
                                <div>
                                    <template v-for="(line, lineIdx) in message.lines" :key="lineIdx">
                                        <div
                                            v-if="shouldRenderLine(message, line)"
                                            :class="['group/user-msg grid auto-rows-min gap-x-3 gap-y-1 place-items-end', isNadiaChatRefreshEnabled ? 'body-small' : 'body']">
                                            <div v-if="isLastIndexOfUserMessage(message.lines, lineIdx) && hasFileActionInLines(message)" class="flex my-1 gap-2 justify-end">
                                                <div class="overflow-auto max-w-xs sm:max-w-lg md:max-w-(--breakpoint-sm) flex gap-2">
                                                    <FileAttachedTag
                                                        v-for="file in getFileActionInLines(message)"
                                                        :key="file.name ?? ''"
                                                        :file="file"
                                                        data-testid="in-chat-file-attached-tag" />
                                                </div>
                                            </div>
                                            <div
                                                :ref="(ref) => setMessageRef(ref, message.chat_message_id)"
                                                :class="[
                                                    'relative flex gap-2 flex-row rounded-lgr',
                                                    isNadiaChatRefreshEnabled ? 'max-w-[80%] p-2 prose-p:my-0' : 'max-w-full px-4 prose-p:my-4',
                                                    {
                                                        'row-start-2': hasFileActionInLines(message),
                                                        'bg-surface-sunken': message.chat_message_id !== focusedMessage,
                                                        'bg-select-option-selected': message.chat_message_id === focusedMessage,
                                                    },
                                                ]">
                                                <BaseButton
                                                    v-if="
                                                        !isNadiaChatRefreshEnabled &&
                                                        editAndStopGeneratingEnabled &&
                                                        isLastIndexOfUserMessage(message.lines, lineIdx) &&
                                                        isLastUserMessage(idx) &&
                                                        !waiting
                                                    "
                                                    theme="tertiary"
                                                    size="sm"
                                                    icon-only
                                                    class="absolute -left-9 top-1/2 -translate-y-1/2 opacity-0 group-hover/user-msg:opacity-100 focus:opacity-100"
                                                    title="Edit message"
                                                    aria-label="Edit message"
                                                    data-testid="edit-message-button"
                                                    @click="handleEditMessage(idx, message)">
                                                    <i class="bi bi-pencil text-sm" aria-hidden="true" />
                                                </BaseButton>
                                                <article class="w-full text-left" :aria-label="`User message ${idx}-${lineIdx}`">
                                                    <ChatMessageText
                                                        v-if="line.type === 'md'"
                                                        :message="line.content"
                                                        :message-id="message.chat_message_id"
                                                        :message-obj="message" />
                                                    <BaseButton
                                                        v-if="isLastIndexOfUserMessage(message.lines, lineIdx) && showRolePlayInsightButton(message)"
                                                        theme="neutral"
                                                        size="sm"
                                                        class="mb-2"
                                                        @click="focusRolePlayFeedback(message.chat_message_id)">
                                                        View suggestion
                                                    </BaseButton>
                                                </article>
                                            </div>
                                            <div
                                                v-if="isNadiaChatRefreshEnabled && isLastIndexOfUserMessage(message.lines, lineIdx) && isLastUserMessage(idx)"
                                                class="flex items-center justify-end gap-1 opacity-100 transition-opacity group-focus-within/user-msg:opacity-100 hover-capable:opacity-0 hover-capable:group-hover/user-msg:opacity-100">
                                                <BaseDate
                                                    v-if="getUserMessageCreatedAt(message)"
                                                    :value="getUserMessageCreatedAt(message)"
                                                    format="relative"
                                                    :now="now"
                                                    :title="formatDate(getUserMessageCreatedAt(message), 'datetime')"
                                                    class="scaffold-details-small text-ink-muted px-1" />
                                                <span v-else class="scaffold-details-small text-ink-muted px-1">{{ t("chat.messageActions.justNow") }}</span>
                                                <BaseButton
                                                    v-if="editAndStopGeneratingEnabled && !waiting"
                                                    theme="tertiary"
                                                    size="sm"
                                                    icon-only
                                                    :title="t('chat.messageActions.editMessage')"
                                                    :aria-label="t('chat.messageActions.editMessage')"
                                                    data-testid="edit-message-button"
                                                    @click="handleEditMessage(idx, message)">
                                                    <i class="bi bi-pencil" aria-hidden="true" />
                                                </BaseButton>
                                                <BaseTooltip
                                                    v-model:open="isUserMessageCopyTooltipOpen"
                                                    :trigger-aria-label="t('chat.messageActions.copyTooltipTrigger')"
                                                    :delay-duration="0"
                                                    @update:open="handleUserCopyTooltipOpen">
                                                    <template #trigger>
                                                        <BaseButton
                                                            theme="tertiary"
                                                            size="sm"
                                                            icon-only
                                                            :aria-label="t('chat.messageActions.copyMessage')"
                                                            data-testid="copy-user-message-button"
                                                            @click="handleCopyUserMessage(message)">
                                                            <i class="bi" :class="isUserMessageCopied ? 'bi-check' : 'bi-copy'" aria-hidden="true" />
                                                        </BaseButton>
                                                    </template>
                                                    <template #content>
                                                        {{ isUserMessageCopied ? t("chat.messageActions.copied") : t("chat.messageActions.copyMessage") }}
                                                    </template>
                                                </BaseTooltip>
                                            </div>
                                            <ChatMyPeopleAccordion
                                                v-if="!isNadiaChatRefreshEnabled && shouldShowConnection(line)"
                                                :execute="execute"
                                                :connection-chat-message-action="{ line: line as ParseActionsTaggedPerson, lineIdx, message }"
                                                data-testid="new-connection"
                                                class="py-2 w-full max-w-92"
                                                should-show-submitted />
                                            <ChatAgentToolBackToPMOverview v-if="lineIsAction(line) && line.action_name === ACTION.BACK_TO_OVERVIEW" :line="line" class="mt-4" />
                                            <ChatAgentToolPushManagerReviewToWorkday
                                                v-if="lineIsAction(line) && line.action_name === ACTION.PUSH_MANAGER_REVIEW_TO_WORKDAY"
                                                :line="line"
                                                :line-idx="lineIdx"
                                                :message-id="message.chat_message_id"
                                                class="mt-4" />
                                            <ChatLoadingFrame
                                                v-if="shouldRenderMemoryLoadingPill(line)"
                                                class="mx-2 mt-4 place-self-start"
                                                :is-loading="!memoryLoadingParams(line).loaded">
                                                {{ memoryLoadingParams(line).content }}
                                            </ChatLoadingFrame>
                                        </div>
                                    </template>
                                </div>
                            </template>
                            <template v-else-if="message.role === 'tool'">
                                <template v-for="(line, lineIdx) in message.lines" :key="lineIdx">
                                    <slot name="tool-result" :line="line" :message="message" :line-idx="lineIdx" />
                                </template>
                            </template>
                        </template>
                    </div>
                </Transition>
                <div aria-live="polite">
                    <NadiaThinkingShapes
                        v-if="isNadiaChatRefreshEnabled && (workIqProgress || shouldShowLoadingMessageMarble || showSlowResponse || isWaitingForInitialResponse || isThinking)"
                        :status-text="workIqProgress?.label ?? ''"
                        :context="thinkingContext"
                        class="my-4 px-2">
                        <BaseChip v-if="workIqProgress?.fromWorkIq" size="xs">
                            <template #leading>
                                <i class="bi bi-diagram-2 text-[15px]/none" aria-hidden="true"></i>
                            </template>
                            {{ t("chat.thinking.workIqChip") }}
                        </BaseChip>
                    </NadiaThinkingShapes>
                    <template v-else-if="!isNadiaChatRefreshEnabled">
                        <ChatWorkIqProgress v-if="workIqProgress" :label="workIqProgress.label" :work-iq-sourced="workIqProgress.fromWorkIq" class="mt-4 px-2" />
                        <CoachingModeMarble v-else-if="shouldShowLoadingMessageMarble" class="mt-4" size="small" is-loading />
                        <ChatWaitingMessage v-else-if="showSlowResponse" :messages="['One moment while I gather my thoughts...']" class="my-4" />
                        <ChatWaitingMessage v-else-if="isWaitingForInitialResponse" :messages="['Waiting for Nadia...']" class="my-4" />
                        <ChatThinkingMessage v-else-if="isThinking" :messages="thinkingMessages" class="my-4" />
                        <GeneralCoachMarble v-else-if="lastMessageIsAssistant" :class="['mt-4 size-(--spacing-awaiting-marble)', { loadingMarble: !lastMessage?.id }]" />
                    </template>
                </div>
                <div ref="bottomAnchor" data-testid="chat-bottom-spacer" class="bottom-anchor pb-10 md:pb-10" :style="{ height: bottomHeight }" />
                <Transition name="fade">
                    <div v-if="!isBottomVisible" class="scroll-btn-rail hidden md:block h-0 sticky bottom-2">
                        <BaseButton
                            theme="neutral"
                            size="sm"
                            :class="[
                                'absolute bottom-0 left-1/2 -translate-x-1/2',
                                { 'scroll-latest-pill inline-flex items-center gap-2 pl-5 pr-4 rounded-full bg-surface-raised text-ink shadow-md': isNadiaChatRefreshEnabled },
                            ]"
                            data-testid="scroll-to-latest-button"
                            @click="scrollToLastMessage">
                            <template v-if="isNadiaChatRefreshEnabled"
                                >{{ t("chat.navigation.scrollToLatest") }} <i class="bi bi-arrow-down leading-none" aria-hidden="true"></i
                            ></template>
                            <template v-else>scroll <i class="bi bi-chevron-down leading-none" aria-hidden="true"></i></template>
                        </BaseButton>
                    </div>
                </Transition>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints, useElementBounding, useElementVisibility, useNow } from "@vueuse/core";
import { useFetch } from "~vue/api";
import GuidedSetupNewChatOffer from "~vue/ChatDetail/GuidedSetupNewChatOffer.vue";
import type { GuidedSetupNewChatOffer as GuidedSetupNewChatOfferProp } from "~vue/ChatDetail/types";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDate from "~vue/components/BaseDate.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import { formatDate } from "~vue/utils/formatDate";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { DateTime } from "luxon";
import { type ComponentPublicInstance, computed, inject, nextTick, onMounted, onUnmounted, provide, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

import { convertToDateTime } from "../dateUtils.js";
import type { GlobalProperties } from "./appTypes.js";
import { ACTION, ChatAction, getRolePlayMonitorParams, isRolePlayMessage, isWorkIqToolName, lineIsAction, pluckMultipleActions } from "./chatActions.js";
import ChatActivation, { isActivationPillShown } from "./ChatDetail/ChatActivation.vue";
import ChatAgentToolBackToPMOverview from "./ChatDetail/ChatAgentToolBackToPMOverview.vue";
import ChatAgentToolPushManagerReviewToWorkday from "./ChatDetail/ChatAgentToolPushManagerReviewToWorkday.vue";
import ChatMyPeopleAccordion from "./ChatDetail/ChatMyPeopleAccordion.vue";
import ChatNadiaIntelligence from "./ChatDetail/ChatNadiaIntelligence.vue";
import SplashScreen from "./ChatDetail/SplashScreen.vue";
import { taggedPeopleKey } from "./ChatDetail/taggedPerson";
import UIDirectiveRenderer from "./ChatDetail/UIDirectiveRenderer.vue";
import ChatLoadingFrame from "./ChatLoadingFrame.vue";
import ChatMessageReactionBar from "./ChatMessageReactionBar.vue";
import ChatMessageText from "./ChatMessageText.vue";
import ChatMessageToolCall, { toolCallHasContent } from "./ChatMessageToolCall.vue";
import ChatThinkingMessage from "./ChatThinkingMessage.vue";
import ChatWaitingMessage from "./ChatWaitingMessage.vue";
import ChatWorkIqProgress from "./ChatWorkIqProgress.vue";
import BaseChip from "./components/BaseChip.vue";
import FileAttachedTag from "./components/FileAttachedTag.vue";
import CoachingModeMarble from "./components/navigation/CoachingModeMarble.vue";
import GeneralCoachMarble from "./components/navigation/CoachingModeMarbles/GeneralCoachMarble.vue";
import { useActionHandler } from "./composables/useActionHandler.js";
import { useNadiaChatRefreshEnabled } from "./composables/useNadiaChatRefresh";
import { nadiaIntelligenceActions, useNadiaIntelligence } from "./composables/useNadiaIntelligence.js";
import { useTimedShow } from "./composables/useTimedShow";
import { inFlightToolName, memoryLoadingParams, toolCallParams, useWorkIqToolProgress } from "./composables/useWorkIqToolProgress";
import { CHAT_EVENT } from "./events";
import NadiaIntelligence from "./icons/NadiaIntelligence.vue";
import NadiaThinkingShapes from "./NadiaThinkingShapes.vue";
import type { ChatMessage, ChatMessageLine, InChatRolePlayData, ParseActionsTaggedPerson } from "./types/chat.js";
import type { ThumbDirection, TurnFeedback } from "./types/feedback.js";
import { hasDirective, resolveDirective, type UiDirectiveAction } from "./uiDirectiveRegistry";

const coachingSessionId = inject<string>("coachingSessionId");
const coachingSessionUuid = inject<string>("coachingSessionUuid");
const artifactsEnabled = inject<boolean>("artifactsEnabled", false);
const { emitter } = inject<GlobalProperties>("globalProperties", {} as GlobalProperties);
const isNadiaChatRefreshEnabled = useNadiaChatRefreshEnabled();
const { t } = useI18n();
const now = useNow({ interval: 60_000 });

type ChatMessageListProps = {
    messages: Array<ChatMessage>;
    // Live progress text for in-flight tool calls, keyed by tool_call_id (see
    // ChatDetail.vue's handleToolCallStatus). Merged into `toolCalls` below.
    toolCallStatusText?: Map<string, string>;
    showOverflow?: boolean;
    showSlowResponse?: boolean;
    showStartLoadingMessage?: boolean;
    showWidgetSidebar?: boolean;
    waiting?: boolean;
    isWaitingForInitialResponse?: boolean;
    isThinking?: boolean;
    userFirstName?: string;
    hasActivatedCoachingSession?: boolean;
    coachingSessionCreatedAt: string;
    showHeader?: boolean;
    hideEmailButton?: boolean;
    editAndStopGeneratingEnabled?: boolean;
    // Boxed PM-chat variant: the list scrolls inside a bordered card (title row above it),
    // so the viewport-cap on .messages and the breakpoint-md centering don't apply.
    boxed?: boolean;
    // Viewport bottom edge of the parent's fixed controls, including their safe-area offset.
    topOverlayBottom?: number;
    // Height of the parent's opaque overlay at the bottom of the viewport; messages under it aren't readable.
    bottomOverlayHeight?: number;
    guidedSetupNewChatOffer?: GuidedSetupNewChatOfferProp | null;
    proactiveCoachingOfferEnabled?: boolean;
    proactiveFeatureOfferEnabled?: boolean;
    // WorkIQ turns replace the per-step status pills with one inline progress line
    // (see useWorkIqToolProgress). Off for everyone else, who keeps the pills.
    workIqConnected?: boolean;
};

const {
    messages = [],
    toolCallStatusText = new Map(),
    showOverflow = true,
    showSlowResponse = false,
    showStartLoadingMessage = false,
    showWidgetSidebar = false,
    waiting = false,
    isWaitingForInitialResponse = false,
    isThinking = false,
    userFirstName,
    hasActivatedCoachingSession = false,
    coachingSessionCreatedAt,
    showHeader = true,
    hideEmailButton = false,
    editAndStopGeneratingEnabled = false,
    boxed = false,
    topOverlayBottom = 0,
    bottomOverlayHeight = 0,
    guidedSetupNewChatOffer = null,
    proactiveCoachingOfferEnabled = false,
    proactiveFeatureOfferEnabled = false,
    workIqConnected = false,
} = defineProps<ChatMessageListProps>();

provide(
    taggedPeopleKey,
    computed(() => pluckMultipleActions(messages, ChatAction.TAGGED_PERSON)),
);

const emit = defineEmits<{
    rolePlayCanceled: [];
    inChatRolePlayStarted: [InChatRolePlayData];
    dismissFailure: [];
    guidedSetupOfferDismissed: [];
}>();

// Whimsical thinking messages during response generation
const thinkingMessages = [
    "Nadia is thinking...",
    "Pondering your question...",
    "Crafting a thoughtful response...",
    "Connecting the dots...",
    "Reflecting on this...",
    "Considering all angles...",
    "Gathering insights...",
    "Thinking it through...",
    "Organizing my thoughts...",
    "Searching for the right words...",
    "Processing this carefully...",
    "Weighing the possibilities...",
    "Exploring different perspectives...",
    "Synthesizing ideas...",
    "Formulating a response...",
    "Drawing from experience...",
    "Tapping into coaching wisdom...",
];

const focusedMessage = ref<number | null>(null);
const { isShowing: isUserMessageCopied, show: showUserMessageCopied, hide: hideUserMessageCopied } = useTimedShow({ duration: 2000 });
const isUserMessageCopyTooltipOpen = ref(false);

const firstCompletedAssistantMessageIndex = computed(() =>
    messages.findIndex(
        (message) =>
            message.role === "assistant" &&
            !message.hidden &&
            Boolean(message.id) &&
            message.lines.some((line) => line.type === "md" && !line.hidden && Boolean(line.content.trim())),
    ),
);

// The offer keeps its place after the user replies; only an explicit dismiss removes it.
const shouldShowNewChatOfferAfter = (message: ChatMessage, messageIndex: number) => Boolean(message.id) && messageIndex === firstCompletedAssistantMessageIndex.value;

// Feedback Relevant
const showReport = ref(false);

// Reaction bar refs for initializing feedback state
const reactionBarRefs = ref<Record<string, Element | ComponentPublicInstance | InstanceType<typeof ChatMessageReactionBar> | null>>({});
const messageFeedback = ref<Record<number, { thumbState: ThumbDirection | null; isSubmitting: boolean }>>({});
const existingFeedback = ref([]);

const getMessageFeedbackState = (messageId: number) => {
    return (
        messageFeedback.value[messageId] || {
            thumbState: null,
            isSubmitting: false,
        }
    );
};

const lastMessage = computed(() => messages.at(-1));

const lastMessageIsAssistant = computed(() => lastMessage.value?.role === "assistant");

const nadiaChatDate = computed(() => {
    const sessionDate = coachingSessionCreatedAt ? convertToDateTime(coachingSessionCreatedAt) : DateTime.now();
    return formatDate(sessionDate.isValid ? sessionDate : DateTime.now(), "date-long");
});

const shouldShowLoadingMessageMarble = computed(() => {
    // Don't show the marble if we're in one of the new thinking states
    if (isWaitingForInitialResponse || isThinking) {
        return false;
    }

    // Show marble if latest message is from user
    return lastMessage.value?.role === "user" && !lastMessage.value.hidden;
});

const loadExistingFeedback = async () => {
    try {
        const { data } = await useFetch<TurnFeedback[]>(`/api/chat/feedback/turn-feedback/coaching-session/${coachingSessionId}/`).get().json();

        const response = data.value;
        existingFeedback.value = response;

        // Initialize reaction bar components with existing feedback
        nextTick(() => {
            response.forEach((feedback: TurnFeedback) => {
                if (feedback.feedback_kind === "thumb" && feedback.feedback_data?.value !== undefined) {
                    const thumbState = feedback.feedback_data.value === 1 ? "up" : "down";
                    // A rated message can lose its bar later (e.g. a role-play setup cancelled after rating),
                    // so skip feedback whose bar isn't mounted rather than abort the remaining entries.
                    const reactionBarRef = reactionBarRefs.value[`rb-${feedback.message_id}`] as InstanceType<typeof ChatMessageReactionBar> | null | undefined;
                    if (reactionBarRef?.initializeFeedbackState) {
                        reactionBarRef.initializeFeedbackState({ thumbState });
                    }
                }
            });
        });
    } catch (error) {
        logError(error, { context: { coaching_session_id: coachingSessionId, operation: "load_existing_feedback" } });
    }
};

// Spacer below the last message, so a freshly sent user message has somewhere to
// scroll to. The full-page chat fills the viewport, so it can size the spacer off
// `dvh`; the boxed variant scrolls inside a card of unknown height, so it measures
// the container instead (see `sizeSpacerToPinLastMessage`).
const bottomHeight = ref<string>("0");
const VIEWPORT_SPACER_HEIGHT = "calc(100dvh - 450px)";
watch(
    () => messages,
    () => {
        if (boxed || messages.length === 0) {
            return;
        }
        const latestMessage = messages[messages.length - 1];
        if (latestMessage.role === "user" && !latestMessage.hidden) {
            // 450px is roughly the height of the top and bottom portions that need to be taken into account, on both desktop and mobile
            bottomHeight.value = VIEWPORT_SPACER_HEIGHT;
        }
    },
    { deep: true },
);
/*
 * The inflated spacer exists only to pin a just-sent user message near the top while Nadia
 * works. Once the turn ends it is blank space below her reply — the ENG-1272 stranding, which
 * `scrollToLastMessage` collapses only when something calls it (chat init, a message edit, the
 * "scroll" button), so a completed turn otherwise leaves the void sitting there. Collapsing it
 * as the turn completes makes the reply the end of the content.
 */
watch(
    () => waiting,
    (isWaiting) => {
        if (isWaiting || boxed) {
            return;
        }
        const latestMessage = messages[messages.length - 1];
        if (latestMessage && (latestMessage.role !== "user" || latestMessage.hidden)) {
            bottomHeight.value = "0";
        }
    },
);
watch(
    () => showStartLoadingMessage,
    (newValue) => {
        if (!newValue) {
            if (!boxed) {
                bottomHeight.value = VIEWPORT_SPACER_HEIGHT;
            }
            nextTick(() => {
                scrollToLastMessage();
            });
        }
    },
    { once: true },
);

onMounted(() => {
    emitter.on(CHAT_EVENT.FOCUS_CHAT_MESSAGE, handleFocusChatMessage);
    emitter.on("show_feedback_report", handleShowReport);
    emitter.on("first_socket_message", loadExistingFeedback);
});

onUnmounted(() => {
    emitter.off(CHAT_EVENT.FOCUS_CHAT_MESSAGE, handleFocusChatMessage);
    emitter.off("show_feedback_report", handleShowReport);
    emitter.off("first_socket_message", loadExistingFeedback);
});

type ToolCallState = {
    name: string;
    completed: boolean;
    action: ChatMessageLine | null;
    messageId: number | null;
    lineIdx: number | null;
    // Live progress text while running (see `toolCallStatusText` prop); null once
    // completed or when no status has arrived yet — ChatMessageToolCall falls back
    // to its static per-tool label in either case.
    statusText: string | null;
};

const toolCalls = computed(() => {
    /*
     * Computes a map that contains all existing tool calls
     * and their associated data. The map looks like this:
     *
     * [key: string]: {
     *    // Name of the tool. Matches the corresponding action's name.
     *    name: String,
     *    // Has the tool completed its run?
     *    completed: Boolean,
     *    // The chat message action containing the tool's returned data
     *    action: ChatMessageAction,
     *    // Message id of the message containing the tool's returned data
     *    messageId: Number,
     *    // Message line index of containing the tool's chat message action
     *    lineIdx: Number
     * }
     *
     * This function runs through `messages` once and constructs the tool call
     * based on the current state of the message stack. The data associated to
     * a tool run is spread across multiple messages. When a tool call is requested,
     * the back-end creates an `assistant` message that has a `tool` line. At this
     * point we know that a tool run for a given action is taking place, and so
     * create the "running" state for the tool in this map. We only know that tool run's
     * id and the name of the action that will eventually unfold.
     *
     * When a tool run completes, the back-end creates a `tool` message that has an
     * `action` line containing the result of the tool call. We must inspect the
     * action's `action_params` to detect a tool call id. If found, the tool call
     * state is updated to mark it as completed and populate the
     * corresponding ChatMessageAction.
     */
    const map = new Map<string, ToolCallState>();
    for (let message of messages) {
        if (!["assistant", "tool"].includes(message.role)) {
            continue;
        }

        if (message.role === "assistant") {
            for (let line of message.lines) {
                if (!lineIsAction(line)) {
                    continue;
                }

                // @ts-expect-error - bringing in types for specific actions (tool call) in smaller pieces
                if (line.action_name === ACTION.TOOL_CALL && !map.has(line.action_params.tool_call_id)) {
                    // @ts-expect-error - bringing in types for specific actions (tool call) in smaller pieces
                    map.set(line.action_params.tool_call_id, {
                        name: line.action_params.tool_name,
                        completed: false,
                        action: null,
                        messageId: null,
                        lineIdx: null,
                        statusText: toolCallStatusText.get(line.action_params.tool_call_id) ?? null,
                    });
                }
            }
        }

        if (message.role === "tool") {
            for (let line of message.lines) {
                if (!lineIsAction(line)) {
                    continue;
                }

                // @ts-expect-error - bringing in types for specific actions (tool call) in smaller pieces
                if (line.action_params?.tool_call_id && map.has(line.action_params.tool_call_id)) {
                    // @ts-expect-error - bringing in types for specific actions (tool call) in smaller pieces
                    map.set(line.action_params.tool_call_id, {
                        // @ts-expect-error - bringing in types for specific actions (tool call) in smaller pieces
                        ...map.get(line.action_params.tool_call_id),
                        completed: true,
                        action: line,
                        messageId: message.chat_message_id,
                        lineIdx: message.lines.indexOf(line),
                    });
                }
            }
        }
    }

    return map;
});

const toolCallForLine = (line: ChatMessageLine): ToolCallState | undefined => {
    const { tool_call_id } = toolCallParams(line);
    return tool_call_id ? toolCalls.value.get(tool_call_id) : undefined;
};

const isToolTurnRunning = (messageIndex: number) => waiting && !messages.slice(messageIndex + 1).some((message) => message.role === "user");

const isCompletedDocumentToolMessage = (message: ChatMessage): boolean => {
    const visibleLines = message.lines.filter((line) => shouldRenderLine(message, line));
    return (
        visibleLines.length > 0 &&
        (isNadiaChatRefreshEnabled.value || !shouldShowNadiaIntelligence(message)) &&
        visibleLines.every((line) => toolCallParams(line).tool_name === ACTION.QUERY_RECENT_DOCUMENT_UPLOADS && toolCallForLine(line)?.completed)
    );
};

// WorkIQ tools report through the single inline progress line at the tail of the stream
// instead of a pill per call, so they never render one — including on an already-finished
// chat, where every WorkIQ pill would otherwise be stale status text (PLT-2308).
const shouldRenderToolCallPill = (line: ChatMessageLine) => {
    if (!lineIsAction(line) || line.action_name !== ACTION.TOOL_CALL || isWorkIqToolName(toolCallParams(line).tool_name)) {
        return false;
    }
    const tool = toolCallForLine(line);
    return tool ? toolCallHasContent(tool, { artifactsEnabled }) : false;
};

// The memory lookup ("Checking my notes...") is never surfaced in the conversation: no pill and
// no empty row (shouldRenderLine defers to this for LOADING actions). WorkIQ users still see it
// folded into the inline progress line, which is rendered separately.
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept as a predicate for shouldRenderLine's call sites
const shouldRenderMemoryLoadingPill = (_line: ChatMessageLine) => false;

const { workIqProgress } = useWorkIqToolProgress(
    () => messages,
    () => toolCallStatusText,
    () => waiting,
    () => workIqConnected,
);

const thinkingContext = computed(() => {
    if (!waiting) {
        return null;
    }
    switch (inFlightToolName(messages)) {
        case ACTION.WORKIQ_UPCOMING_CALENDAR:
            return "calendar";
        case ACTION.EXPLORE_COLLABORATION:
            return "perspective";
        default:
            return null;
    }
});

const messageRefs = ref<Record<string, Element | ComponentPublicInstance>>({});
const refForMessage = (chatMessageId: number) => `m-${chatMessageId}`;
const setMessageRef = (ref: Element | ComponentPublicInstance | null, chatMessageId: number) => {
    if (ref) {
        messageRefs.value[refForMessage(chatMessageId)] = ref;
    }
};

const handleShowReport = () => {
    logUserInteraction("feedback_report_toggled", {}, coachingSessionUuid);
    showReport.value = !showReport.value;
};

// Long sessions render markdown/code blocks/tool calls across multiple frames,
// so the target message ref may not be registered when handleFocusChatMessage
// first fires. Poll for ~1s, then re-check viewport after layout settles in
// case content above the target grew and pushed it out of view.
const FOCUS_POLL_MAX_ATTEMPTS = 10;
const FOCUS_POLL_INTERVAL_MS = 100;
const FOCUS_SETTLE_RECHECK_MS = 600;
const FOCUS_HIGHLIGHT_DURATION_MS = 1000;

const handleFocusChatMessage = ({ chatMessageId }: { chatMessageId: number }) => {
    scrollToMessageWhenReady(chatMessageId, FOCUS_POLL_MAX_ATTEMPTS);
};

const scrollToMessageWhenReady = (chatMessageId: number, attemptsLeft: number) => {
    const messageRef = messageRefs.value[refForMessage(chatMessageId)];
    if (!(messageRef instanceof HTMLElement)) {
        if (attemptsLeft > 0) {
            setTimeout(() => scrollToMessageWhenReady(chatMessageId, attemptsLeft - 1), FOCUS_POLL_INTERVAL_MS);
        }
        return;
    }
    messageRef.scrollIntoView({ block: "center", behavior: "smooth" });
    focusedMessage.value = chatMessageId;
    nextTick(() => {
        setTimeout(() => {
            focusedMessage.value = null;
        }, FOCUS_HIGHLIGHT_DURATION_MS);
    });
    // Re-scroll after layout has had a chance to settle, in case messages above
    // the target grew during the smooth-scroll animation and shifted it out of view.
    setTimeout(() => {
        const settledRef = messageRefs.value[refForMessage(chatMessageId)];
        const root = scrollRoot.value;
        if (!(settledRef instanceof HTMLElement) || !root) {
            return;
        }
        const rootRect = root.getBoundingClientRect();
        const rect = settledRef.getBoundingClientRect();
        const inViewport = rect.top >= rootRect.top && rect.bottom <= rootRect.bottom;
        if (!inViewport) {
            settledRef.scrollIntoView({ block: "center", behavior: "smooth" });
        }
    }, FOCUS_SETTLE_RECHECK_MS);
};

const showRolePlayInsightButton = (message: ChatMessage) => {
    if (!isRolePlayMessage(message)) {
        return false;
    }

    const params = getRolePlayMonitorParams(message);
    return params && !!params.feedback;
};

const focusRolePlayFeedback = (chatMessageId: number) => {
    // Open the sidebar if it's closed, and focus the feedback on the next tick.
    emitter.emit(CHAT_EVENT.FOCUS_WIDGET, { actionName: ACTION.ROLE_PLAY_MONITOR });
    nextTick(() => {
        emitter.emit(CHAT_EVENT.FOCUS_ROLE_PLAY_MESSAGE_FEEDBACK, chatMessageId);
    });
};

const handleThoughtProcessClicked = (message: ChatMessage) => {
    emitter.emit(CHAT_EVENT.OPEN_THOUGHT_PROCESS_DIALOG, message.id);
};

// The bar reacts to the whole message, so it goes on the last line that shows the user something
// (prose or a card). Rendering it per line repeats it around an email draft card, and anchoring it
// to prose alone leaves it above a card that closes the message.
const lastReactionBarLine = (message: ChatMessage, lines: ChatMessageLine[]) =>
    lines.findLastIndex((line) => shouldRenderLine(message, line) && (line.type === "md" || shouldShowUiDirective(line)));

const shouldIncludeReactionBar = (message: ChatMessage, lineIdx: number) => {
    if (!message.lines) {
        return;
    }
    const isCoach = message.role === "assistant";
    const isLastTextLine = lastReactionBarLine(message, message.lines) === lineIdx;
    const isFinishedRendering = !!message.id; // partial chat additions don't yet have a message.id, this comes back from `chat_message` event
    return isCoach && isLastTextLine && isFinishedRendering;
};

const isLastIndexOfUserMessage = (lines: ChatMessageLine[], idx: number) => {
    const lastNonAction = lines.findLastIndex((l) => l.type !== "action");
    // File-only message (no typed content): anchor decorations like the
    // FileAttachedTag chip to the first line instead of returning -1, which
    // would suppress rendering entirely.
    if (lastNonAction === -1) {
        return idx === 0;
    }
    return lastNonAction === idx;
};

const isLastUserMessage = (msgIdx: number) => {
    for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === "user") {
            return i === msgIdx;
        }
    }
    return false;
};

const getUserMessageCreatedAt = (message: ChatMessage) => {
    if (!message.created_at) {
        return null;
    }
    try {
        const createdAt = convertToDateTime(message.created_at);
        return createdAt.isValid ? createdAt : null;
    } catch {
        return null;
    }
};

const handleUserCopyTooltipOpen = (open = false) => {
    isUserMessageCopyTooltipOpen.value = open;
    if (!open) {
        hideUserMessageCopied();
    }
};

const handleCopyUserMessage = async (message: ChatMessage) => {
    const content = message.lines
        .filter((line) => line.type === "md")
        .map((line) => line.content)
        .join("\n\n");
    if (!content) {
        return;
    }

    await globalThis.navigator.clipboard.writeText(content);
    showUserMessageCopied();
    isUserMessageCopyTooltipOpen.value = true;
    logUserInteraction(
        "user_message_copied",
        {
            chat_message_id: message.chat_message_id,
        },
        coachingSessionUuid,
    );
};

const handleEditMessage = (msgIdx: number, message: ChatMessage) => {
    const mdLines = message.lines.filter((l) => l.type === "md");
    const content = mdLines.map((l) => ("content" in l ? l.content : "")).join("\n");
    emitter?.emit(CHAT_EVENT.EDIT_MESSAGE, {
        messageIdx: msgIdx,
        content,
        chatMessageUuid: message.chat_message_uuid,
    });
};
const hasFileActionInLines = (message: ChatMessage) =>
    message.lines.some((l) => (l?.type === "action" && l?.action_name === ACTION.FILE_UPLOAD) || (message.files?.length ?? 0) > 0);
const getFileActionInLines = (message: ChatMessage) => {
    const filesFromActionParams = message.lines.find((l) => l?.type === "action" && l?.action_name === ACTION.FILE_UPLOAD && l?.action_params.length > 0);

    if (filesFromActionParams?.type === "action" && filesFromActionParams.action_name === ACTION.FILE_UPLOAD) {
        return filesFromActionParams.action_params ?? message.files;
    }
    return message.files;
};

const bottom = useTemplateRef("bottomAnchor");
const showActivationPill = computed(() => !showStartLoadingMessage && !hasActivatedCoachingSession && isActivationPillShown(messages));
const isBelowMd = useBreakpoints(breakpointsTailwind).smaller("md");
// Scrolls the chat stream to the latest message. Two branches:
//
// 1. Mobile, last message is from the user: anchor that message near the top
//    of the scroll container, below the measured fade and floating controls. We
//    scroll the container directly rather than calling scrollIntoView on the
//    message element, because iOS Safari otherwise chains the scroll onto the
//    visual viewport and drifts fixed top-of-viewport elements off-screen.
//
//    The boxed PM variant takes this branch at every breakpoint: its scroll
//    container is a card rather than the viewport, so the viewport-sized spacer
//    the other branch relies on overshoots and carries the message off the top.
//
// 2. Full-page chat, last message is not a visible user message (Nadia's reply,
//    tool results, hidden context messages): collapse the now-stale spacer, then
//    scroll the bottomAnchor into view with block: "end", so the scroll ends at
//    real content instead of blank space (ENG-1272). Boxed is excluded — its
//    spacer is measured to the card, not viewport-sized (ENG-1542).
//
// 3. Everything else (desktop visible user message, initial load, boxed with a
//    non-user tail): scroll the bottomAnchor into view with block: "end", which
//    is the historical behavior.
const scrollRoot = ref<HTMLElement | null>(null);
const { bottom: topFadeBottom, update: measureTopFade } = useElementBounding(useTemplateRef("topFade"));
const { bottom: activationPillBottom, update: measureActivationPill } = useElementBounding(useTemplateRef("activationPill"));
const pinClearance = (root: HTMLElement) => {
    const padding = parseFloat(getComputedStyle(root).scrollPaddingTop) || 0;
    if (boxed) {
        if (!showActivationPill.value) {
            return padding;
        }
        measureActivationPill();
        return Math.max(padding, activationPillBottom.value - root.getBoundingClientRect().top);
    }
    // Read after keyboard/layout reflow; the fade is teleported outside the scroll root.
    measureTopFade();
    return Math.max(padding, Math.max(topFadeBottom.value, topOverlayBottom) - root.getBoundingClientRect().top);
};

// Grow the spacer to exactly the room the message needs to reach the top of the
// container — no more, so scrolling to the bottom can't strand the reader in blank space.
const sizeSpacerToPinLastMessage = (el: HTMLElement, root: HTMLElement) => {
    const spacer = bottom.value;
    if (!spacer) {
        return;
    }
    const contentBelowMessageTop = spacer.offsetTop - el.offsetTop;
    bottomHeight.value = `${Math.max(0, root.clientHeight - pinClearance(root) - contentBelowMessageTop)}px`;
};

const pinMessageToTop = (el: HTMLElement, root: HTMLElement) => {
    const offset = el.getBoundingClientRect().top - root.getBoundingClientRect().top;
    root.scrollTo({ top: root.scrollTop + offset - pinClearance(root), behavior: "smooth" });
};

const scrollToLastMessage = () => {
    const lastMessage = messages[messages.length - 1];
    const root = scrollRoot.value as unknown as HTMLElement | null;
    if ((isBelowMd.value || boxed) && root && lastMessage?.role === "user" && !lastMessage.hidden) {
        const el = messageRefs.value[refForMessage(lastMessage.chat_message_id)];
        if (el instanceof HTMLElement) {
            if (boxed) {
                sizeSpacerToPinLastMessage(el, root);
                // Let the taller spacer land before scrolling, or the scroll clamps to the old height.
                nextTick(() => pinMessageToTop(el, root));
                return;
            }
            pinMessageToTop(el, root);
            return;
        }
    }
    if (!boxed && lastMessage && (lastMessage.role !== "user" || lastMessage.hidden)) {
        // The viewport-sized spacer was inflated to pin the user's just-sent message near
        // the top and nothing collapses it once anything else becomes the latest message
        // (Nadia's reply, a tool result), so scrolling to the anchor's end would strand
        // the view in blank space below the content — and keep the "scroll" button
        // visible over that blank space (ENG-1272). Collapse it first so the anchor's end
        // is the end of real content. Boxed is excluded: its spacer is measured to the
        // card, not viewport-sized (ENG-1542), so it keeps the historical scroll. The
        // `hidden` check is defensive parity with the watcher and pin branch above: the
        // server currently drops hidden messages before they reach the client, so it
        // can't fire today.
        bottomHeight.value = "0";
        // Let the collapsed spacer land before scrolling, or the target position is stale.
        nextTick(() => bottom.value?.scrollIntoView({ behavior: "smooth", block: "end" }));
        return;
    }
    bottom.value?.scrollIntoView({ behavior: "smooth", block: "end" });
};

// Keep the assistant clear of the same overlays when the soft keyboard reflows the viewport.
const pinLatestAssistantToTop = () => {
    const root = scrollRoot.value as unknown as HTMLElement | null;
    if (!root) {
        return;
    }
    for (let i = messages.length - 1; i >= 0; i--) {
        const msg = messages[i];
        if (msg.role !== "assistant" || msg.hidden) {
            continue;
        }
        const el = messageRefs.value[refForMessage(msg.chat_message_id)];
        if (el instanceof HTMLElement) {
            pinMessageToTop(el, root);
        }
        return;
    }
};
defineExpose({ scrollToLastMessage, pinLatestAssistantToTop });

/*
 * A WorkIQ turn's tool-call messages have no renderable lines at all now. The block would still
 * render empty and still take a `gap-10` slot between messages, which is the blank line left
 * behind when the progress indicator goes away. Nadia Intelligence lives inside the same block
 * and is suppressed at the line level, so it has to keep the block alive on its own.
 */
const hasRenderableAssistantContent = (message: ChatMessage) =>
    message.lines.some((line) => shouldRenderLine(message, line)) || (!isNadiaChatRefreshEnabled.value && shouldShowNadiaIntelligence(message));

const shouldRenderLine = (message: ChatMessage, line: ChatMessageLine) => {
    if (lineIsAction(line)) {
        if (isNadiaChatRefreshEnabled.value && line.action_name === ACTION.TAGGED_PERSON) {
            return false;
        }
        if (hasDirective(line.action_name)) {
            return shouldShowUiDirective(line);
        }
        // A tool call or memory lookup renders exactly when its pill does. Neither does on a
        // WorkIQ turn (the inline progress line covers them), and a line that renders nothing
        // still emits a row — an empty bubble under the user's own text, in the memory case.
        if (line.action_name === ACTION.TOOL_CALL) {
            return shouldRenderToolCallPill(line);
        }
        if (line.action_name === ACTION.LOADING) {
            return shouldRenderMemoryLoadingPill(line);
        }
        // Actions handled by ChatNadiaIntelligence are suppressed on assistant messages
        // to avoid empty ghost rows — they render via the Nadia Intelligence collapsible instead.
        if (nadiaIntelligenceActions.includes(line.action_name)) {
            return message.role !== "assistant";
        }
        return line.action_name !== ChatAction.ROLE_PLAY_ADJUST;
    }
    return line.type !== "tool";
};

const isBottomVisible = useElementVisibility(bottom, { rootMargin: () => `0px 0px -${bottomOverlayHeight}px 0px` });

// The overlay grows upward as a draft wraps onto more lines. When the reader was at the end of the
// stream, follow the growth so the latest message stays clear of it instead of sliding underneath.
watch(
    () => bottomOverlayHeight,
    (height, previousHeight) => {
        const root = scrollRoot.value;
        const growth = height - previousHeight;
        if (!root || growth <= 0) {
            return;
        }
        const distanceFromEnd = root.scrollHeight - root.clientHeight - root.scrollTop;
        if (distanceFromEnd <= growth + 2) {
            root.scrollTop += growth;
        }
    },
    { flush: "post" },
);

const handleRolePlayStarted = (data: InChatRolePlayData) => {
    emit("inChatRolePlayStarted", data);
};

const handleRolePlayCanceled = () => {
    emit("rolePlayCanceled");
};

const shouldShowConnection = (line: ChatMessageLine) => {
    return lineIsAction(line) && line.action_name === ACTION.TAGGED_PERSON;
};

// A cancelled role-play setup renders nothing, so it must not keep its assistant row or reaction bar.
const isDismissedRolePlaySetup = (line: ChatMessageLine) => lineIsAction(line) && line.action_name === ChatAction.ROLE_PLAY_SETUP && !!line.action_state?.dismissed;

const shouldShowUiDirective = (line: ChatMessageLine): line is UiDirectiveAction =>
    lineIsAction(line) && !isDismissedRolePlaySetup(line) && resolveDirective(line.action_name, { proactiveCoachingOfferEnabled, proactiveFeatureOfferEnabled }) !== null;
const { execute } = useActionHandler();
const { isNadiaIntelligenceOpen, setIsNadiaIntelligenceOpen, shouldShowNadiaIntelligence } = useNadiaIntelligence();
</script>

<style scoped>
@reference "../../css/tailwind.css";

@layer components {
    /* Offset the fixed message gap only around document cards; replies also have paragraph spacing. */
    .chat-message-stack > * + .document-tool-message {
        @apply -mt-5;
    }

    .chat-message-stack > .document-tool-message:not(:last-child) {
        @apply -mb-10;
    }

    .chat-message-stack > .document-tool-message + .document-tool-message {
        @apply mt-3;
    }
}

.chat_component {
    :deep(.prose) {
        font-size: inherit;
    }
    :deep(.prose p) {
        word-break: break-word;
        font-size: inherit;
    }
}

.chat_component .messages {
    height: 100%;
    max-height: 80vh;
}

/* Boxed variant: the surrounding card bounds the height, so the viewport cap would
   truncate the scrollable area. */
.chat_component.boxed .messages {
    max-height: none;
}

/* Refreshed chat: the flex scroll container already bounds the height, so the 80vh cap
   strands the stream above the composer instead of letting it scroll behind it. */
.refreshed-chat .messages {
    max-height: none;
}

/* Refreshed full-page chat (desktop): ChatDetail pins the composer as an opaque overlay and
   publishes its height as `--composer-overlay-h`, so pad the stream to clear it and float the
   scroll-to-latest rail just above it. Unlayered so it beats the `pb-10` / `bottom-2` utilities. */
.refreshed-chat:not(.boxed) .bottom-anchor {
    @variant md {
        padding-bottom: calc(var(--composer-overlay-h, 4rem) + 2rem);
    }
}

.refreshed-chat:not(.boxed) .scroll-btn-rail {
    @variant md {
        bottom: calc(var(--composer-overlay-h, 4rem) + 0.5rem);
    }
}

/* Small App type: `base-btn-sm` is already 14/20 sans, so only its weight token needs to drop to regular. */
@layer components {
    .scroll-latest-pill {
        --font-weight-button-sm: var(--font-weight-normal);
    }
}

.blurEffect {
    @apply absolute h-16 top-0 left-0 w-full pointer-events-none;
    background: linear-gradient(180deg, var(--color-surface), transparent);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 200ms ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.loadingMarble {
    animation: spinningMarble 2s cubic-bezier(0.65, 0, 0.25, 1.1) infinite;
    animation-delay: 300ms;
}

@keyframes spinningMarble {
    100% {
        transform: rotate(360deg);
    }
}
</style>
